import { useRef, useState } from 'react';
import api from '../../services/api';
import { trackEvent } from '../../utils/analytics';

/**
 * ImportWizard — smart CSV import with column mapping confirmation.
 *
 * Step 1: Upload CSV → server parses, infers column mapping, returns raw rows
 * Step 2: Map Columns — user confirms/adjusts which CSV column maps to each field
 *         Live preview table reads from rawSampleRows using current mapping (no server call)
 * Step 3: Result (imported / skipped / errors)
 */

const BROKER_NAMES = {
  'interactive-brokers': 'Interactive Brokers',
  'thinkorswim': 'thinkorswim (TD Ameritrade)',
  'webull': 'Webull',
  'robinhood': 'Robinhood',
  'schwab': 'Charles Schwab',
  'alpaca': 'Alpaca',
  'generic': 'Unknown format',
};

const SUPPORTED_BROKERS = [
  'Interactive Brokers', 'thinkorswim', 'Webull',
  'Robinhood', 'Charles Schwab', 'Alpaca', 'any CSV or Excel',
];

// Fields the user maps. Required fields block import if unset.
const MAPPING_FIELDS = [
  { key: 'symbol',     label: 'Symbol',       required: true,  hint: 'Stock ticker (AAPL, TSLA)' },
  { key: 'side',       label: 'Side',         required: true,  hint: 'BUY or SELL direction' },
  { key: 'quantity',   label: 'Quantity',     required: true,  hint: 'Number of shares/contracts' },
  { key: 'price',      label: 'Price',        required: true,  hint: 'Average fill price' },
  { key: 'date',       label: 'Date / Time',  required: true,  hint: 'Trade execution time' },
  { key: 'commission', label: 'Commission',   required: false, hint: 'Fees paid (optional)' },
];

export default function ImportWizard() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);          // full server response
  const [columnMapping, setColumnMapping] = useState({}); // user-editable mapping
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  function reset() {
    setStep(1); setFile(null); setPreview(null);
    setColumnMapping({}); setResult(null); setError(null);
  }

  async function handlePreview(f) {
    setLoading(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await api.post('/import/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setPreview(data.data);
        setFile(f);
        // Pre-fill mapping from server inference
        setColumnMapping(data.data.columnMapping || {});
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse CSV');
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!file) return;
    setLoading(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('columnMapping', JSON.stringify(columnMapping));
      const { data } = await api.post('/import/confirm', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        trackEvent('trade_imported', {
          broker: preview?.broker || 'csv',
          imported: data.data?.imported || 0,
          skipped: data.data?.skipped || 0,
        });
        setResult(data.data);
        setStep(3);
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Import failed');
      }
    } finally {
      setLoading(false);
    }
  }

  // Sample values for a field: pull from rawSampleRows using current mapping
  function sampleValues(fieldKey) {
    const col = columnMapping[fieldKey];
    if (!col || !preview?.rawSampleRows?.length) return '—';
    const vals = preview.rawSampleRows
      .slice(0, 3)
      .map(r => r[col])
      .filter(v => v && v.toString().trim())
      .map(v => v.toString().trim());
    return vals.length ? vals.join(' · ') : '—';
  }

  const requiredMapped = MAPPING_FIELDS
    .filter(f => f.required)
    .every(f => !!columnMapping[f.key]);

  const allHeaders = preview?.headers || [];

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">CSV Trade Import</h1>
          <p className="text-gray-400 text-sm mt-1">
            Supports {SUPPORTED_BROKERS.join(' · ')}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {['Upload', 'Map Columns', 'Result'].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step > i + 1 ? 'bg-green-600 text-white' :
                step === i + 1 ? 'bg-blue-600 text-white' :
                'bg-gray-700 text-gray-400'
              }`}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span className={`text-sm ${step === i + 1 ? 'text-white font-medium' : 'text-gray-500'}`}>
                {label}
              </span>
              {i < 2 && <div className={`flex-1 h-px w-8 ${step > i + 1 ? 'bg-green-600' : 'bg-gray-700'}`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 mb-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="bg-gray-900 border border-white/10 rounded-xl p-8">
            <div
              className="border-2 border-dashed border-gray-600 rounded-xl p-12 text-center cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) handlePreview(f);
              }}
            >
              <div className="text-4xl mb-3">📤</div>
              <p className="text-white font-medium mb-1">Drop your CSV file here</p>
              <p className="text-gray-400 text-sm">or click to browse — max 10 MB</p>
              <p className="text-gray-600 text-xs mt-3">
                .csv · .xlsx · .xls · .txt &nbsp;|&nbsp; {SUPPORTED_BROKERS.join(', ')}
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              className="hidden"
              onChange={e => {
                const f = e.target.files[0];
                if (f) handlePreview(f);
              }}
            />
            {loading && (
              <p className="text-center text-blue-400 text-sm mt-4 animate-pulse">
                Detecting format...
              </p>
            )}
          </div>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && preview && (
          <div className="space-y-4">

            {/* Broker detection badge */}
            <div className="bg-gray-900 border border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Detected Format</p>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${preview.broker === 'generic' ? 'text-amber-400' : 'text-green-400'}`}>
                    {BROKER_NAMES[preview.broker] || preview.broker}
                  </span>
                  {preview.broker !== 'generic' && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">auto-detected</span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Rows in file</p>
                <p className="text-xl font-bold">{(preview.rawSampleRows?.length < 5 ? preview.rawSampleRows?.length : preview.totalRows) || '—'}</p>
              </div>
            </div>

            {/* Warnings */}
            {preview.warnings?.length > 0 && (
              <div className="space-y-1">
                {preview.warnings.map(w => (
                  <p key={w} className="text-xs text-amber-400 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <span className="mt-0.5">⚠</span> {w}
                  </p>
                ))}
              </div>
            )}

            {/* Column mapping table */}
            <div className="bg-gray-900 border border-white/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/10">
                <p className="text-sm font-medium text-white">Column Mapping</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Match each trade field to the correct column in your CSV.
                  Sample values update as you change selections.
                </p>
              </div>
              <div className="divide-y divide-white/5">
                {MAPPING_FIELDS.map(field => (
                  <div key={field.key} className="px-5 py-3 grid grid-cols-[140px_1fr_1fr] gap-4 items-center">
                    {/* Field label */}
                    <div>
                      <p className="text-sm font-medium text-white">
                        {field.label}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </p>
                      <p className="text-xs text-gray-600">{field.hint}</p>
                    </div>

                    {/* CSV column selector */}
                    <select
                      value={columnMapping[field.key] || ''}
                      onChange={e => setColumnMapping(m => ({ ...m, [field.key]: e.target.value || null }))}
                      className={`bg-gray-800 border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        field.required && !columnMapping[field.key]
                          ? 'border-amber-500/60'
                          : 'border-white/10'
                      }`}
                    >
                      <option value="">— not in file —</option>
                      {allHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>

                    {/* Live sample values */}
                    <div className="text-xs text-gray-400 truncate" title={sampleValues(field.key)}>
                      {sampleValues(field.key)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={reset}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || !requiredMapped}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Importing...' : !requiredMapped ? 'Map all required fields to continue' : `Import Trades`}
              </button>
            </div>

            {!requiredMapped && (
              <p className="text-xs text-center text-amber-400">
                Assign a CSV column to all required fields (*) before importing.
              </p>
            )}
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="bg-gray-900 border border-white/10 rounded-xl p-8 text-center">
            <div className="text-5xl mb-4">{result.imported > 0 ? '✅' : '⚠️'}</div>
            <h2 className="text-xl font-bold text-white mb-2">Import Complete</h2>

            <div className="flex justify-center gap-6 my-6">
              <div>
                <p className="text-3xl font-bold text-green-400">{result.imported}</p>
                <p className="text-xs text-gray-500 mt-0.5">Imported</p>
              </div>
              {(result.duplicates ?? 0) > 0 && (
                <div>
                  <p className="text-3xl font-bold text-gray-400">{result.duplicates}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Already existed</p>
                </div>
              )}
              {(result.invalid ?? 0) > 0 && (
                <div>
                  <p className="text-3xl font-bold text-amber-400">{result.invalid}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Could not parse</p>
                </div>
              )}
            </div>

            {/* Invalid rows diagnostic */}
            {result.invalid > 0 && result.imported === 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm text-amber-400 font-medium mb-2">
                  All rows were skipped — column mapping may not match your file's data.
                </p>
                {result.sampleInvalid?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 font-medium">First failed rows:</p>
                    {result.sampleInvalid.map((s, i) => (
                      <div key={i} className="bg-gray-800/60 rounded px-3 py-2">
                        <p className="text-xs text-amber-300">{s.reason}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {Object.entries(s.raw || {}).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  Go back and verify the column mapping — especially Price and Quantity columns.
                </p>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-left">
                <p className="text-xs text-red-400 font-medium mb-1">{result.errors.length} row(s) errored:</p>
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-300">{e}</p>
                ))}
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={result.imported === 0 && result.invalid > 0 ? () => setStep(2) : reset}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors"
              >
                {result.imported === 0 && result.invalid > 0 ? '← Fix Mapping' : 'Import Another File'}
              </button>
              {result.imported > 0 && (
                <a
                  href="/trades"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors"
                >
                  View Trades →
                </a>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
